import {
  EUserAccessPart,
  EWorkspaceProjectRole,
  IProjectsResponseDto,
  IUserInfoDto,
  IWorkspaceProjectRole
} from "@kuika/kuika-cl-model";
import * as React from "react";
import { ProjectService } from "../../../services/project-service";
import { UserService } from "../../../services/user-service";
import { KIcon } from "../../../shared/icon/k-icon";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaRoleBadge from "../../ui-kit/kuika-role-badge/kuika-role-badge";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { EMemberInfoType } from "./member-info-modal";

// Selecting every role at once. Null rather than a magic string so a real role
// can never collide with it.
const ALL_ROLES: null = null;

export interface IUserProjectRole {
  id: string;
  userId: string;
  projectId: string;
  projectName: string;
  role: string;
}

interface IRoleGroup {
  role: string;
  rows: IUserProjectRole[];
}

export interface IState {
  projectRoleList: IWorkspaceProjectRole[];
  userProjectRoleList: IUserProjectRole[];
  projectMeta: Record<string, IProjectsResponseDto>;
  roleFilter: string | null;
}

export interface IProps {
  profileInfo: IUserInfoDto;
  type: EMemberInfoType;
  // My profile only. GetMyProjects returns the VIEWER's apps, so joining it in
  // while an admin looks at somebody else would attach the wrong icons and open
  // the wrong screens.
  enrichWithMyProjects?: boolean;
  onOpenProject?: (screenId: string) => void;
}

class ProfileUserProjects extends React.PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      projectRoleList: [],
      userProjectRoleList: [],
      projectMeta: {},
      roleFilter: ALL_ROLES
    };
  }

  async componentDidMount() {
    // The rows come off the profile payload already in hand, so they are drawn before
    // anything is asked of the network rather than behind the role list.
    if (this.props.profileInfo?.projectRoles) {
      this.loadUserProjectList();
    }
    // Neither request feeds the other — the role list fills the Change-role menu and the
    // meta join supplies icons — so they run together. They used to be two awaits in a row,
    // which on a workspace with hundreds of apps meant the second one started only after
    // the first had come back.
    const pending: Promise<void>[] = [this.loadProjectRoleList()];
    if (this.props.enrichWithMyProjects) {
      pending.push(this.loadProjectMeta());
    }
    await Promise.all(pending);
  }

  componentDidUpdate(prevProps: Readonly<IProps>): void {
    if (prevProps.profileInfo !== this.props.profileInfo) {
      this.loadUserProjectList();
    }
  }

  async loadProjectRoleList(): Promise<void> {
    try {
      const response = await ProjectService.GetProjectRoleList();
      const roles: IWorkspaceProjectRole[] = response.data?.filter(
        (role) => role.enumId !== EWorkspaceProjectRole.ProjectOwner
      );
      this.setState({ projectRoleList: roles });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  }

  // The app icon and the screen to open both live on the workspace project list,
  // not on the profile payload — this joins the two on projectId rather than
  // asking the backend for a new shape.
  loadProjectMeta = async () => {
    try {
      const response = await ProjectService.GetMyProjects();
      const meta: Record<string, IProjectsResponseDto> = {};
      (response.data ?? []).forEach((project: IProjectsResponseDto) => {
        if (project.id) meta[project.id.toLowerCase()] = project;
      });
      this.setState({ projectMeta: meta });
    } catch (error) {
      // A failed join costs the icons and the click target, not the list.
      console.error("Could not load app details for the profile list:", error);
    }
  };

  loadUserProjectList = (): void => {
    // Copied, not aliased: assignRoleToUser writes `role` back onto these
    // objects, and the array used to come straight out of the context's
    // userInfo — so changing a role silently edited global state.
    const source: IUserProjectRole[] = (this.props.profileInfo?.projectRoles ?? []) as unknown as IUserProjectRole[];
    const rows: IUserProjectRole[] = source.map((row: IUserProjectRole): IUserProjectRole => ({ ...row }));
    this.setState({ userProjectRoleList: rows });
  };

  private metaOf(projectId: string): IProjectsResponseDto | undefined {
    if (!projectId) return undefined;
    return this.state.projectMeta[projectId.toLowerCase()];
  }

  private openProject = (row: IUserProjectRole): void => {
    const meta = this.metaOf(row.projectId);
    const screenId: string | undefined = meta?.lastVisitedScreenId || meta?.defaultScreenDesignId;
    if (!screenId || !this.props.onOpenProject) return;
    this.props.onOpenProject(screenId.toLowerCase());
  };

  private canOpen(row: IUserProjectRole): boolean {
    if (!this.props.onOpenProject) return false;
    const meta = this.metaOf(row.projectId);
    return Boolean(meta?.lastVisitedScreenId || meta?.defaultScreenDesignId);
  }

  // Rows are grouped by role so the role stops printing 34 times. With a single
  // group the heading is redundant too — the list header carries it instead.
  private groupByRole(rows: IUserProjectRole[]): IRoleGroup[] {
    const order: string[] = [];
    const byRole: Record<string, IUserProjectRole[]> = {};
    rows.forEach((row: IUserProjectRole) => {
      const key: string = row.role ?? "";
      if (!byRole[key]) {
        byRole[key] = [];
        order.push(key);
      }
      byRole[key].push(row);
    });
    return order.map((role: string): IRoleGroup => ({ role, rows: byRole[role] }));
  }

  /** The role this member holds on that project, as a menu key. */
  getCurrentProjectRoleKey = (projectId: string): string | null => {
    const currentRole: string | undefined = this.state.userProjectRoleList.find(
      (userProjectRole: IUserProjectRole): boolean => userProjectRole.projectId === projectId
    )?.role;
    const match: IWorkspaceProjectRole | undefined = (this.state.projectRoleList ?? []).find(
      (role: IWorkspaceProjectRole): boolean => role.name === currentRole
    );
    return match?.id ?? null;
  };

  getChangeProjectRoleOptions = (userId: string, projectId: string): KuikaDropdownMenuItemType[] => {
    const hasAccessRemoveUser: boolean = KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.DeleteRole);

    const roleOptions: KuikaDropdownMenuItemType[] = (this.state.projectRoleList ?? []).map(
      (role: IWorkspaceProjectRole): KuikaDropdownMenuItemType => ({
        key: role.id,
        label: role.name,
        // The primitive marks the held role itself — its own tick, its own accent, its own
        // indent. This used to paste a raw `check.svg` into the icon slot at a hand-written
        // 16px, which left the row looking like a list with one stray glyph in front of it.
        showSelectedIcon: true,
        // Its own group, so the primitive draws the rule the removal row used to carry itself.
        group: 1,
        onClick: (): void => {
          this.assignRoleToUser({ userId, projectId, roleId: role.enumId });
        }
      })
    );

    if (!hasAccessRemoveUser) return roleOptions;

    return [
      ...roleOptions,
      {
        key: "delete",
        label: "Remove user",
        danger: true,
        group: 2,
        onClick: (): void => {
          this.removeUserFromProject({ userId, projectId });
        }
      }
    ];
  };

  removeUserFromProject = async (data: { userId: string; projectId: string }) => {
    try {
      const res = await UserService.RemoveUserFromProject(data.userId, data.projectId);
      if (res && res.status === 200) {
        KMainFunctions.displaySuccessNotification("User removed from project successfully");
        this.setState((prevState) => ({
          userProjectRoleList: prevState.userProjectRoleList.filter(
            (userProjectRole) => userProjectRole.projectId !== data.projectId
          )
        }));
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  assignRoleToUser = async (data: { userId: string; projectId: string; roleId: EWorkspaceProjectRole }) => {
    try {
      const res = await UserService.AssignProjectRoleToUser(data.userId, data.projectId, data.roleId);
      if (res && res.status === 200) {
        KMainFunctions.displaySuccessNotification("User role assigned successfully");
        const userProjectRoleList = [...this.state.userProjectRoleList];
        const userProjectRoleIndex = userProjectRoleList.findIndex(
          (userProjectRole) => userProjectRole.projectId === data.projectId
        );
        if (userProjectRoleIndex !== -1) {
          const assignedRole: IWorkspaceProjectRole | undefined = this.state.projectRoleList.find(
            (role) => role.enumId === data.roleId
          );
          userProjectRoleList[userProjectRoleIndex].role = assignedRole?.name ?? "";
          this.setState({ userProjectRoleList });
        }
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  // No icon data for this app: a hue derived from its name still gives the row
  // something to recognise, and it is stable across sessions.
  private renderIcon(row: IUserProjectRole): JSX.Element {
    const meta = this.metaOf(row.projectId);
    if (meta?.iconAsImage) {
      return <img className="project__icon" src={meta.iconAsImage} alt="" />;
    }
    if (meta?.iconName) {
      return (
        <span className="project__icon" style={{ backgroundColor: meta.iconBackgroundColor }}>
          <KIcon iconName={meta.iconName} style={{ color: meta.iconColor, fontSize: 14 }} />
        </span>
      );
    }
    const name: string = row.projectName ?? "";
    let hue = 0;
    for (let i = 0; i < name.length; i++) hue = (hue * 31 + name.charCodeAt(i)) % 360;
    return (
      <span className="project__icon project__icon--mono" style={{ backgroundColor: `oklch(0.58 0.15 ${hue})` }}>
        {name.slice(0, 2).toUpperCase()}
      </span>
    );
  }

  private renderRow(row: IUserProjectRole, hasAccessChangeRole: boolean): JSX.Element {
    const isEditable: boolean = hasAccessChangeRole && row.role !== "Project Owner";
    const openable: boolean = this.canOpen(row);
    return (
      <div className={`user-projects__project${openable ? " is-openable" : ""}`} key={row.id ?? row.projectId}>
        {this.renderIcon(row)}
        {openable ? (
          // The label is the row's hit area: its ::after covers the whole row,
          // so the pointer means the same thing everywhere on it. The text is
          // wrapped so the button can let that overlay escape its own overflow.
          <button type="button" className="project__name" onClick={() => this.openProject(row)}>
            <span>{row.projectName}</span>
          </button>
        ) : (
          <span className="project__name">{row.projectName}</span>
        )}
        <span className="project__end">
          {isEditable && (
            <KuikaDropdownMenu
              options={this.getChangeProjectRoleOptions(row.userId, row.projectId)}
              selected={this.getCurrentProjectRoleKey(row.projectId)}
              trigger="click"
              header="Choose role"
              overlayClassName="kuika_profile__workspace-role-dropdown"
            >
              <button type="button" className="project__role">
                <KuikaRoleBadge
                  role={row.role}
                  rightIcon={<KuikaSvg name="chevron-down" type="arrows" width={11} height={11} />}
                  // The row is keyed by its membership, and picking a role from this menu changes
                  // it in place — the badge is where the change is confirmed.
                  animateChanges
                />
              </button>
            </KuikaDropdownMenu>
          )}
          {openable && (
            <svg className="project__go" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M9 6l6 6-6 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
      </div>
    );
  }

  render() {
    const hasAccessChangeRole = KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.EditProjectRole);
    const all: IUserProjectRole[] = this.state.userProjectRoleList ?? [];
    // Roles are counted over the whole list, never over the current selection,
    // so the counts do not move when you pick one.
    const roles: IRoleGroup[] = this.groupByRole(all);
    const isSingleRole: boolean = roles.length === 1;
    const active: string | null = this.state.roleFilter;
    const rows: IUserProjectRole[] = active === ALL_ROLES ? all : all.filter((row) => row.role === active);
    const shown: IRoleGroup[] = this.groupByRole(rows);
    // Headings would repeat the selected chip, so they only appear while the
    // list is showing more than one role at once.
    const showGroupHeadings: boolean = shown.length > 1;

    return (
      <div className="kuika_profile__user-projects">
        {/* Role, not search. A text filter over app names made this a small copy
            of the workspace Apps page; the thing a profile answers is where this
            person is NOT the owner. */}
        <div className="user-projects__header">
          <span className="user-projects__label">
            <span data-testid="appsText">Apps</span>
            {/* The All chip carries the total when the chips are drawn, so the
                header would be stating it twice. */}
            {isSingleRole && all.length > 0 && <span className="user-projects__count">{all.length}</span>}
            {isSingleRole && roles[0].role && <span className="user-projects__all-role">· all {roles[0].role}</span>}
          </span>
          {!isSingleRole && (
            <div className="user-projects__roles" role="group" aria-label="Filter by role">
              <button
                type="button"
                className={active === ALL_ROLES ? "is-active" : undefined}
                aria-pressed={active === ALL_ROLES}
                onClick={() => this.setState({ roleFilter: ALL_ROLES })}
              >
                All<b>{all.length}</b>
              </button>
              {roles.map((group: IRoleGroup) => (
                <button
                  key={group.role}
                  type="button"
                  className={active === group.role ? "is-active" : undefined}
                  aria-pressed={active === group.role}
                  onClick={() => this.setState({ roleFilter: group.role })}
                >
                  {group.role}
                  <b>{group.rows.length}</b>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="user-projects__scroll">
          {rows.length === 0 && <div className="user-projects__empty">No apps yet.</div>}
          {shown.map((group: IRoleGroup) => (
            <React.Fragment key={group.role}>
              {showGroupHeadings && (
                <div className="user-projects__group">
                  <span>{group.role}</span>
                  <b>{group.rows.length}</b>
                </div>
              )}
              {group.rows.map((row: IUserProjectRole) => this.renderRow(row, hasAccessChangeRole))}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }
}

const profileUserProjects = ProfileUserProjects;
export { profileUserProjects as ProfileUserProjects };
